import React from 'react';
import { useTranslation } from 'localization';
import { MultiSelect } from 'primereact/multiselect';
import CollapsibleSectionAtom from '../../atoms/CollapsibleSection/CollapsibleSectionAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { FactorFormState } from '../../../types/statusFactors';
import type { ArcAggregationMethod, ArcSelection } from 'types/spherePoints';
import './ArcHealthEditorMolecule.scss';

export interface ArcSummary {
  arcId: string;
  displayName: string;
  isPrimary?: boolean;
  templateSystemName?: string;
}

export interface ArcTemplateSummary {
  templateSystemName: string;
  displayName: string;
}

export interface ArcHealthEditorMoleculeProps {
  formState: FactorFormState;
  onChange: (updates: Partial<FactorFormState>) => void;
  disabled?: boolean;
  className?: string;
  arcsAvailable?: ArcSummary[];
  arcTemplatesAvailable?: ArcTemplateSummary[];
}

const ArcHealthEditorMolecule: React.FC<ArcHealthEditorMoleculeProps> = ({
  formState,
  onChange,
  disabled = false,
  className,
  arcsAvailable = [],
  arcTemplatesAvailable = [],
}) => {
  const { t } = useTranslation();

  const arcSelection: ArcSelection = formState.arcSelection ?? { type: 'primary' };
  const arcAggregationMethod: ArcAggregationMethod = formState.arcAggregationMethod ?? 'worst';

  const classes = ['arc-health-editor', className].filter(Boolean).join(' ');

  const selectionTypeOptions = [
    { value: 'primary', label: t('statusFactors.arcHealth.arcSelection.primary'), disabled },
    { value: 'all', label: t('statusFactors.arcHealth.arcSelection.all'), disabled },
    { value: 'specific', label: t('statusFactors.arcHealth.arcSelection.specific'), disabled },
    { value: 'by_template', label: t('statusFactors.arcHealth.arcSelection.byTemplate'), disabled },
  ];

  const aggregationOptions = [
    { value: 'worst', label: t('statusFactors.arcHealth.aggregation.worst'), disabled },
    { value: 'best', label: t('statusFactors.arcHealth.aggregation.best'), disabled },
    { value: 'weighted_average', label: t('statusFactors.arcHealth.aggregation.weightedAverage'), disabled },
  ];

  const handleSelectionTypeChange = (selectionType: string | string[]) => {
    const type = selectionType as ArcSelection['type'];
    if (type === 'primary' || type === 'all') {
      onChange({ arcSelection: { type } });
    } else if (type === 'specific') {
      const existingIds = arcSelection.type === 'specific' ? arcSelection.arcIds : [];
      onChange({ arcSelection: { type: 'specific', arcIds: existingIds } });
    } else if (type === 'by_template') {
      const existingNames = arcSelection.type === 'by_template' ? arcSelection.templateSystemNames : [];
      onChange({ arcSelection: { type: 'by_template', templateSystemNames: existingNames } });
    }
  };

  const handleAggregationChange = (method: string | string[]) => {
    onChange({ arcAggregationMethod: method as ArcAggregationMethod });
  };

  const handleSpecificArcsChange = (e: { value: string[] }) => {
    onChange({ arcSelection: { type: 'specific', arcIds: e.value } });
  };

  const handleTemplatesChange = (e: { value: string[] }) => {
    onChange({ arcSelection: { type: 'by_template', templateSystemNames: e.value } });
  };

  return (
    <div className={classes}>
      {/* How it works */}
      <CollapsibleSectionAtom
        title={t('statusFactors.arcHealth.howItWorks')}
        defaultExpanded={false}
        className="arc-health-editor__section"
      >
        <div className="arc-health-editor__how-it-works">
          <ul>
            <li>{t('statusFactors.arcHealth.howItWorks1')}</li>
            <li>{t('statusFactors.arcHealth.howItWorks2')}</li>
            <li>{t('statusFactors.arcHealth.howItWorks3')}</li>
            <li>{t('statusFactors.arcHealth.howItWorks4')}</li>
          </ul>
        </div>
      </CollapsibleSectionAtom>

      {/* Arc Selection */}
      <CollapsibleSectionAtom
        title={t('statusFactors.arcHealth.arcSelection.label')}
        defaultExpanded={true}
        className="arc-health-editor__section"
      >
        <div className="arc-health-editor__form">
          <div className="form-field">
            <small className="form-hint">{t('statusFactors.arcHealth.arcSelection.hint')}</small>
            <SegmentedControlAtom
              options={selectionTypeOptions}
              value={arcSelection.type}
              onChange={handleSelectionTypeChange}
              mode="single"
              variant="pills"
              className="arc-health-editor__selection-picker"
              ariaLabel={t('statusFactors.arcHealth.arcSelection.label')}
            />
          </div>

          {arcSelection.type === 'specific' && (
            <div className="form-field">
              <small className="form-hint">{t('statusFactors.arcHealth.arcSelection.specificHint')}</small>
              <MultiSelect
                value={arcSelection.arcIds}
                options={arcsAvailable.map((a) => ({ label: a.displayName, value: a.arcId }))}
                onChange={handleSpecificArcsChange}
                placeholder={t('statusFactors.arcHealth.arcSelection.selectArcs')}
                disabled={disabled}
                className="arc-health-editor__multiselect"
                display="chip"
              />
            </div>
          )}

          {arcSelection.type === 'by_template' && (
            <div className="form-field">
              <small className="form-hint">{t('statusFactors.arcHealth.arcSelection.byTemplateHint')}</small>
              <MultiSelect
                value={arcSelection.templateSystemNames}
                options={arcTemplatesAvailable.map((tpl) => ({ label: tpl.displayName, value: tpl.templateSystemName }))}
                onChange={handleTemplatesChange}
                placeholder={t('statusFactors.arcHealth.arcSelection.selectTemplates')}
                disabled={disabled}
                className="arc-health-editor__multiselect"
                display="chip"
              />
            </div>
          )}
        </div>
      </CollapsibleSectionAtom>

      {/* Aggregation Method */}
      <CollapsibleSectionAtom
        title={t('statusFactors.arcHealth.aggregation.label')}
        defaultExpanded={true}
        className="arc-health-editor__section"
      >
        <div className="arc-health-editor__form">
          <div className="form-field">
            <small className="form-hint">{t('statusFactors.arcHealth.aggregation.hint')}</small>
            <SegmentedControlAtom
              options={aggregationOptions}
              value={arcAggregationMethod}
              onChange={handleAggregationChange}
              mode="single"
              variant="pills"
              className="arc-health-editor__aggregation-picker"
              ariaLabel={t('statusFactors.arcHealth.aggregation.label')}
            />
          </div>
        </div>
      </CollapsibleSectionAtom>
    </div>
  );
};

ArcHealthEditorMolecule.displayName = 'ArcHealthEditorMolecule';

export default ArcHealthEditorMolecule;
